import React, { useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "@tanstack/react-router";
import { usePortfolioStore, type Project } from "./stores/portfolioStore";
import BannerComponent from "./components/BannerComponent";
import { Header, Footer, BackToTopBtn } from "./components/layout";
import ScrollGuide from "./components/motion/ScrollGuide";
import ModeChooser from "./components/ModeChooser";
import { hasSeenChooser, isBot } from "./lib/chooserSession";
import { useLenis } from "./hooks/useLenis";
import { scrollToElement } from "./lib/motion";
import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";

const App: React.FC = () => {
  const location = useLocation();
  const { navbarBackground, pageLoaded, setPageLoaded, handleScroll } =
    usePortfolioStore();

  useLenis();

  const bannerRef = useRef<HTMLDivElement>(null);
  const [bannerOverflow, setBannerOverflow] = useState(0);
  const isHome = location.pathname === "/";

  // Decided once on mount: opening the chooser marks it seen, which would
  // otherwise flip this gate mid-render.
  const [chooserEligible, setChooserEligible] = useState(
    () => isHome && !location.hash && !isBot() && !hasSeenChooser()
  );

  useEffect(() => {
    if (!isHome) setChooserEligible(false);
  }, [isHome]);

  // Sticky offset lets the full hero scroll into view before it pins
  useEffect(() => {
    const banner = bannerRef.current;
    if (!isHome || !banner) return;

    const measure = () => {
      setBannerOverflow(Math.max(0, banner.offsetHeight - window.innerHeight));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(banner);
    window.addEventListener("resize", measure);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [isHome]);

  // Fallback to hide loader after a maximum time
  useEffect(() => {
    const fallbackTimer = setTimeout(() => {
      setPageLoaded(true);
    }, 5000); // Hide loader after 5 seconds maximum

    return () => clearTimeout(fallbackTimer);
  }, [setPageLoaded]);

  // Scroll effects
  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Load projects data
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const projectsData = await import("./components/projects.json");
        usePortfolioStore
          .getState()
          .setProjects(projectsData.data.list as Project[]);
        usePortfolioStore.getState().setLoading(false);
        // Hide page loader after projects are loaded
        setTimeout(() => {
          setPageLoaded(true);
        }, 800); // Slightly longer delay for better UX
      } catch (error) {
        console.error("Failed to load projects:", error);
        usePortfolioStore.getState().setLoading(false);
        // Still hide loader even if projects fail to load
        setTimeout(() => {
          setPageLoaded(true);
        }, 500);
      }
    };

    loadProjects();
  }, [setPageLoaded]);

  // Smooth scroll functions (Lenis-aware)
  const scrollToContact = () => {
    scrollToElement("#book-consult");
  };

  const scrollToBody = () => {
    scrollToElement("#process");
  };

  return (
    <div id="app">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-cyan-500 focus:text-slate-950 focus:rounded-lg focus:font-semibold"
      >
        Skip to main content
      </a>

      <Header
        navbarBackground={navbarBackground}
        scrollToContact={scrollToContact}
      />

      {isHome && (
        <div
          ref={bannerRef}
          className="relative lg:sticky lg:top-[var(--banner-pin-top)] hero-pin w-full z-[1] lg:mb-0 -mb-12"
          style={
            { "--banner-pin-top": `${-bannerOverflow}px` } as React.CSSProperties
          }
        >
          <BannerComponent scrollToBody={scrollToBody} />
        </div>
      )}

      <main id="main-content" className="relative w-full z-10 dark:bg-slate-900">
        <div className="body_cont pt-4">
          <div className="container">
            <Outlet />
          </div>
        </div>

        <Footer scrollToContact={scrollToContact} />

        <div className={`page_loader ${pageLoaded ? "hidden" : ""}`}>
          <div className="matrix-bg"></div>
          <div className="loading-bubbles">
            <div className="loading-bubble"></div>
            <div className="loading-bubble"></div>
            <div className="loading-bubble"></div>
          </div>
          <div className="loading-text">Initializing portfolio...</div>
        </div>

        <BackToTopBtn />
        <ScrollGuide />

        <div className="mouseover_checker"></div>
      </main>

      {chooserEligible && (
        <ModeChooser
          ready={pageLoaded}
          onClose={() => setChooserEligible(false)}
        />
      )}
    </div>
  );
};

export default App;
